// src/features/fb/cookies/questions/CookiesQuestions.jsx
import React, { useState, useRef } from "react";
import QuestionsSection from "../../../../shared/sections/QuestionsSection";
import PartNameQuestion from "./PartNameQuestion";
import MaterialSelectionQuestionCorrugated from "./MaterialSelectionQuestionCorrugated";
import BatchSizeQuestion from "./BatchSizeQuestion";
import PartWeightDimensionsQuestion from "./PartWeightDimensionsQuestion";
import PartDetailsQuestion from "./PartDetailsQuestion";
import PrintingDetails from "./PrintingDetails";
import ManufacturingCountrySelect from "./ManufacturingCountrySelect";

/**
 * CookiesQuestionsSection Component
 *
 * Encapsulates all product-related questions for Cookies commodity
 * Manages form data, validation, and question flow
 *
 * @param {function} onComplete - Callback when all questions are answered
 * @param {function} onDataChange - Callback when form data changes
 * @param {object} initialData - Initial data to populate the form
 */
export default function CorrugatedQuestions({ onComplete, onDataChange, initialData }) {
  const [productFormData, setProductFormData] = useState(
    initialData || {
      productName: "",
      materialSelection: "",
      batchSize: "",
      partWeightDimensions: {
        partLength: "",
        partWidth: "",
        partHeight: "",
      },
      partDetails: {
        wall: "",
        regrindPct: "",
        avgWallThickness: "",
        burstingFactor: "",
        noOfPly: "",
        flute: "",
        printing: "",
        numberOfColors: "",
        printingCoverage: "",
        inkCoverage: "",
      },
      marketPositioning: "",
      skuWeight: "",
      skuWeightUnit: "g",
      annualVolume: "",
      manufacturingCountry: "",
    }
  );

  // Calculate initial step based on answered questions
  const getInitialStep = () => {
    if (!initialData) return 0;

    if (initialData.manufacturingCountry) return 6; // Last question
    if (initialData.partDetails?.printing) return 5; // Printing Details
    if (initialData.partDetails) return 4; // Part Details
    if (initialData.partWeightDimensions) return 3;
    if (initialData.batchSize) return 2;
    if (initialData.materialSelection) return 1;
    if (initialData.productName) return 0;
    return 0;
  };

  const [productChatStep, setProductChatStep] = useState(getInitialStep());
  const [demoMode, setDemoMode] = useState(false);
  
  // Refs for validation
  const materialSelectionRef = useRef(null);
  const partDetailsRef = useRef(null);
  const printingDetailsRef = useRef(null);

  // Check if all questions are answered
  const isAllAnswered =
    productFormData.productName?.trim() &&
    productFormData.materialSelection?.trim() &&
    productFormData.batchSize &&
    parseFloat(productFormData.batchSize) > 0 &&
    productFormData.partWeightDimensions?.partLength &&
    productFormData.partWeightDimensions?.partWidth &&
    productFormData.partWeightDimensions?.partHeight &&
    productFormData.manufacturingCountry;

  // Notify parent when data changes and all questions are answered
  React.useEffect(() => {
    if (isAllAnswered) {
      onDataChange?.(productFormData);
    }
    // Don't call onDataChange(null) when incomplete - this would wipe parent state
    // Parent should retain the last valid data for persistence across navigation
  }, [productFormData, isAllAnswered, onDataChange]);

  // Selected values for display - only show values that have been answered
  const productSelectedValues = [
    productFormData.productName && {
      label: "Part Name:",
      value: productFormData.productName,
    },
    productFormData.materialSelection && {
      label: "Material:",
      value: productFormData.materialSelection,
    },
    productFormData.batchSize && {
      label: "Batch Size:",
      value: productFormData.batchSize,
    },
    // Only show Part Dimensions after step 2 (Part Weight Dimensions question) is answered
    productFormData.partWeightDimensions?.partLength &&
      productFormData.partWeightDimensions?.partWidth &&
      productFormData.partWeightDimensions?.partHeight && {
        label: "Part Dimensions:",
        value: `L: ${productFormData.partWeightDimensions.partLength}mm, W: ${
          productFormData.partWeightDimensions.partWidth
        }mm, H: ${productFormData.partWeightDimensions.partHeight}mm`,
      },
    // Show Part Details if any field has a value (excluding printing details)
    (productFormData.partDetails?.wall ||
      productFormData.partDetails?.regrindPct ||
      productFormData.partDetails?.avgWallThickness ||
      productFormData.partDetails?.burstingFactor ||
      productFormData.partDetails?.noOfPly ||
      productFormData.partDetails?.flute) && {
        label: "Part Details:",
        value: [
          productFormData.partDetails?.wall && `Wall: ${productFormData.partDetails.wall}`,
          productFormData.partDetails?.regrindPct && `Regrind: ${productFormData.partDetails.regrindPct}%`,
          productFormData.partDetails?.avgWallThickness &&
            `ECT: ${productFormData.partDetails.avgWallThickness}`,
          productFormData.partDetails?.burstingFactor &&
            `Bursting Factor: ${productFormData.partDetails.burstingFactor}`,
          productFormData.partDetails?.noOfPly &&
            `No of ply: ${productFormData.partDetails.noOfPly}`,
          productFormData.partDetails?.flute &&
            `Flute: ${productFormData.partDetails.flute}`,
        ]
          .filter(Boolean)
          .join(", "),
      },
    // Show Printing Details if any field has a value
    (productFormData.partDetails?.printing ||
      productFormData.partDetails?.numberOfColors ||
      productFormData.partDetails?.printingCoverage ||
      productFormData.partDetails?.inkCoverage) && {
        label: "Printing Details:",
        value: [
          productFormData.partDetails?.printing && `Printing: ${productFormData.partDetails.printing}`,
          productFormData.partDetails?.numberOfColors && `No of colors: ${productFormData.partDetails.numberOfColors}`,
          productFormData.partDetails?.printingCoverage && `Printing Coverage: ${productFormData.partDetails.printingCoverage}%`,
          productFormData.partDetails?.inkCoverage && `Ink coverage: ${productFormData.partDetails.inkCoverage}%`,
        ]
          .filter(Boolean)
          .join(", "),
      },
    productFormData.manufacturingCountry && {
      label: "Manufacturing Country:",
      value: productFormData.manufacturingCountry,
    },
  ].filter(Boolean); // Remove falsy values

  // Validation function to check if next button should be disabled
  const isNextDisabled = (step) => {
    switch (step) {
      case 0: // Product Name
        return !productFormData.productName || productFormData.productName.trim() === "";
      case 1: // Material Selection
        return !productFormData.materialSelection || productFormData.materialSelection.trim() === "";
      case 2: // Batch Size
        return (
          !productFormData.batchSize ||
          productFormData.batchSize.trim() === "" ||
          parseFloat(productFormData.batchSize) <= 0
        );
      case 3: // Part Weight and Dimensions
        return (
          !productFormData.partWeightDimensions?.partLength ||
          !productFormData.partWeightDimensions?.partWidth ||
          !productFormData.partWeightDimensions?.partHeight
        );
      case 4: // Part Details
        // Check if all Part Details fields are filled
        const partDetails = productFormData.partDetails || {};
        const WALL_OPTIONS = ["Single Wall", "Double Wall", "Triple Wall"];
        
        // Wall is required and must be a valid option
        if (!partDetails.wall || partDetails.wall.trim() === "") return true;
        if (!WALL_OPTIONS.some(option => option === partDetails.wall.trim())) return true;
        
        // ETC (avgWallThickness) is required
        if (!partDetails.avgWallThickness || partDetails.avgWallThickness.toString().trim() === "") return true;
        
        // Bursting Factor is required
        if (!partDetails.burstingFactor || partDetails.burstingFactor.toString().trim() === "") return true;
        
        // No of ply is required
        if (!partDetails.noOfPly || partDetails.noOfPly.toString().trim() === "") return true;
        
        // All fields are filled
        return false;
      case 5: // Printing Details
        // Check if printing is selected
        const printing = productFormData.partDetails?.printing || "";
        const PRINTING_OPTIONS = ["Flexography Printing", "Offset Printing", "NA"];
        if (!printing || (typeof printing === "string" && printing.trim() === "")) return true;
        const trimmedPrinting = typeof printing === "string" ? printing.trim() : String(printing).trim();
        if (!PRINTING_OPTIONS.some(option => option === trimmedPrinting)) return true;
        // If printing is not "NA", all other fields are required
        if (trimmedPrinting !== "NA") {
          const numberOfColors = productFormData.partDetails?.numberOfColors;
          if (numberOfColors === undefined || numberOfColors === null || String(numberOfColors).trim() === "") return true;
          
          const printingCoverage = productFormData.partDetails?.printingCoverage;
          if (printingCoverage === undefined || printingCoverage === null || String(printingCoverage).trim() === "") return true;
          
          const inkCoverage = productFormData.partDetails?.inkCoverage;
          if (inkCoverage === undefined || inkCoverage === null || String(inkCoverage).trim() === "") return true;
        }
        return false;
      case 6: // Manufacturing Country
        return !productFormData.manufacturingCountry;
      default:
        return false;
    }
  };

  const handleComplete = () => {
    onComplete?.(productFormData);
  };

  // Custom step change handler that validates before moving to next step
  const handleStepChange = async (newStep) => {
    // If moving forward from Material Selection step (step 1), validate first
    if (productChatStep === 1 && newStep > productChatStep) {
      if (materialSelectionRef.current && materialSelectionRef.current.validate) {
        const isValid = await materialSelectionRef.current.validate();
        if (!isValid) {
          // Validation failed, don't proceed to next step
          return;
        }
      }
    }
    // Validation passed or not needed, proceed to next step
    setProductChatStep(newStep);
  };
  
  // Helper function to handle automatic navigation to next step on Enter key
  const handleNextStep = async (stepIndex) => {
    // Use handleStepChange to ensure consistent validation logic
    const nextStep = stepIndex + 1;
    const totalSteps = 7; // Total number of questions (includes PrintingDetails)
    
    if (nextStep < totalSteps) {
      // Use handleStepChange which handles validation
      await handleStepChange(nextStep);
    } else {
      handleComplete();
    }
  };

  // Reset function to clear all fields and start from first question
  const handleReset = () => {
    setProductFormData({
      productName: "",
      materialSelection: "",
      batchSize: "",
      partWeightDimensions: {
        partLength: "",
        partWidth: "",
        partHeight: "",
      },
      partDetails: {
        wall: "",
        regrindPct: "",
        avgWallThickness: "",
        burstingFactor: "",
        noOfPly: "",
        flute: "",
        printing: "",
        numberOfColors: "",
        printingCoverage: "",
        inkCoverage: "",
      },
      marketPositioning: "",
      skuWeight: "",
      skuWeightUnit: "g",
      annualVolume: "",
      manufacturingCountry: "",
    });
    setProductChatStep(0);
  };

  // Handle demo toggle
  const handleDemoToggle = (checked) => {
    setDemoMode(checked);
    if (checked) {
      // Set demo values when toggle is turned on
      setProductFormData({
        productName: "BOX",
        materialSelection: "Kraft",
        batchSize: "9000",
        partWeightDimensions: {
          partLength: "1200",
          partWidth: "1200",
          partHeight: "300",
        },
        partDetails: {
          wall: "Single Wall",
          regrindPct: "",
          avgWallThickness: "32",
          burstingFactor: "30",
          noOfPly: "3",
          flute: "C",
          printing: "Flexography Printing",
          numberOfColors: "1",
          printingCoverage: "5",
          inkCoverage: "5",
        },
        marketPositioning: "",
        skuWeight: "",
        skuWeightUnit: "g",
        annualVolume: "",
        manufacturingCountry: "China",
      });
      // Navigate to the last step to show all filled values
      setProductChatStep(6);
    } else {
      // Reset form when toggle is turned off
      handleReset();
    }
  };

  return (
    <QuestionsSection
      selectedValues={productSelectedValues}
      currentStep={productChatStep}
      onStepChange={handleStepChange}
      onComplete={handleComplete}
      brandColor="#ff6034"
      isNextDisabled={isNextDisabled}
      onReset={handleReset}
      demoMode={demoMode}
      onDemoToggle={handleDemoToggle}
    >
      <PartNameQuestion
        value={productFormData.productName}
        onChange={(value) => setProductFormData({ ...productFormData, productName: value })}
      />
      <MaterialSelectionQuestionCorrugated
        ref={materialSelectionRef}
        value={productFormData.materialSelection}
        onChange={(value) => setProductFormData({ ...productFormData, materialSelection: value })}
        onNextStep={() => handleNextStep(1)}
        currentStep={productChatStep}
      />
      <BatchSizeQuestion
        value={productFormData.batchSize}
        onChange={(value) => setProductFormData({ ...productFormData, batchSize: value })}
      />
      <PartWeightDimensionsQuestion
        value={productFormData.partWeightDimensions}
        onChange={(value) =>
          setProductFormData({ ...productFormData, partWeightDimensions: value })
        }
      />
      <PartDetailsQuestion
        ref={partDetailsRef}
        value={productFormData.partDetails}
        onChange={(value) =>
          setProductFormData({ ...productFormData, partDetails: value })
        }
      />
      <PrintingDetails
        ref={printingDetailsRef}
        value={
          productFormData.partDetails?.printing || 
          productFormData.partDetails?.numberOfColors ||
          productFormData.partDetails?.printingCoverage ||
          productFormData.partDetails?.inkCoverage
            ? {
                printing: productFormData.partDetails?.printing || "",
                numberOfColors: productFormData.partDetails?.numberOfColors || "",
                printingCoverage: productFormData.partDetails?.printingCoverage || "",
                inkCoverage: productFormData.partDetails?.inkCoverage || "",
              }
            : ""
        }
        onChange={(value) => {
          // Determine the printing value
          const printingValue = typeof value === "string" ? value : value?.printing || "";
          
          // If printing is "NA", automatically set other fields to 0
          if (printingValue === "NA") {
            setProductFormData({
              ...productFormData,
              partDetails: {
                ...productFormData.partDetails,
                printing: printingValue,
                numberOfColors: 0,
                printingCoverage: 0,
                inkCoverage: 0,
              },
            });
          } else {
            // Otherwise, use the values from the component
            setProductFormData({
              ...productFormData,
              partDetails: {
                ...productFormData.partDetails,
                printing: printingValue,
                numberOfColors: typeof value === "object" ? value?.numberOfColors || "" : "",
                printingCoverage: typeof value === "object" ? value?.printingCoverage || "" : "",
                inkCoverage: typeof value === "object" ? value?.inkCoverage || "" : "",
              },
            });
          }
        }}
      />
      <ManufacturingCountrySelect
        value={productFormData.manufacturingCountry}
        onChange={(value) =>
          setProductFormData({ ...productFormData, manufacturingCountry: value })
        }
      />
    </QuestionsSection>
  );
}
